import React, { useState } from 'react';
import api from '../utils/api';

const CHANGE_TYPE_LABELS = {
  new_starter: 'New starter',
  leaver: 'Leaver',
  pension: 'Pension update',
  salary: 'Salary change',
  parental_leave: 'Parental leave',
  sick_leave: 'Sick leave',
  unpaid_leave: 'Unpaid leave',
  addition: 'Addition',
  deduction: 'Deduction',
  bank_details: 'Bank details',
  address_change: 'Address change',
  personal_details: 'Personal details',
  contractual_change: 'Contractual change',
};

function formatWhen(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.valueOf())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// One correction the payroll team applied on the client's behalf, with an
// editable note for where it came from. The note is filled in here rather than at
// the moment of correction: logging a WhatsApp correction shouldn't stop to ask
// for paperwork, but by the time you're reconciling against BrightPay it matters
// that the row can be explained.
function AmendmentRow({ amendment, onSourceSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(amendment.amendment_source || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.patch(`/admin/amendments/${amendment.id}/source`, {
        amendment_source: draft.trim() || null,
      });
      onSourceSaved(amendment.id, res.data?.amendment_source ?? null);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const when = formatWhen(amendment.amended_at);

  return (
    <li className="py-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-[12px] font-semibold text-sky-800 dark:text-sky-300">
          {CHANGE_TYPE_LABELS[amendment.change_type] || amendment.change_type}
        </span>
        <span className="text-[11.5px] text-gray-500">
          logged by {amendment.amended_by_name || 'the payroll team'}
          {when ? ` · ${when}` : ''}
        </span>
      </div>

      {editing ? (
        <div className="flex items-center gap-2 mt-1">
          <input
            type="text"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') { setEditing(false); setDraft(amendment.amendment_source || ''); }
            }}
            placeholder="e.g. WhatsApp from Jane, 15 Sep"
            className="flex-1 min-w-0 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-[12.5px] focus:border-brand-500 focus:ring-brand-500"
          />
          <button type="button" onClick={save} disabled={saving}
            className="px-2 py-1 rounded text-[12px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => { setEditing(false); setDraft(amendment.amendment_source || ''); }}
            className="px-2 py-1 rounded text-[12px] text-gray-500 hover:text-gray-700">
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setEditing(true)}
          className="text-left text-[12px] mt-0.5 hover:underline">
          {amendment.amendment_source
            ? <span className="text-gray-600 dark:text-gray-300 italic">{amendment.amendment_source}</span>
            : <span className="text-sky-700 dark:text-sky-400">+ where did this come from?</span>}
        </button>
      )}
      {error && <p className="text-[11.5px] text-rose-600 mt-0.5">{error}</p>}
    </li>
  );
}

// Corrections applied to this employee after the client had already submitted.
// They carry no money of their own — the change is already reflected in the
// figures above — so this is purely "this number moved, and here's why".
export default function AdminAmendmentList({ amendments = [], onSourceSaved }) {
  if (!amendments.length) return null;
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-sky-300 dark:border-sky-700">
      <div className="text-sky-700 dark:text-sky-400 uppercase tracking-wider text-[12px] font-semibold mb-1">
        Corrections logged after submission
      </div>
      <p className="text-[12px] text-gray-500 mb-1.5">
        Applied on the client&apos;s behalf, so they aren&apos;t in the submission above. The figures already
        include them.
      </p>
      <ul className="divide-y divide-sky-100 dark:divide-sky-900/40">
        {amendments.map(a => (
          <AmendmentRow key={a.id} amendment={a} onSourceSaved={onSourceSaved} />
        ))}
      </ul>
    </div>
  );
}
